import React, { useEffect, useState, useContext, useCallback } from 'react';
import { Redirect, useHistory, useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { Loader } from '@jutro/components';
import { Wizard } from 'wmic-pe-portals-custom-wizard-react';
import { useAuthentication } from '@xengage/gw-digital-auth-react';
import { TranslatorContext } from '@jutro/locale';
import { useDependencies } from '@xengage/gw-portals-dependency-react';
import { ErrorBoundary } from '@xengage/gw-portals-error-react';
import { messages as platformMessages } from '@xengage/gw-platform-translations';
import { ViewModelServiceContext, withViewModelService } from '@xengage/gw-portals-viewmodel-react';
import { WizardModalsProvider, useWizardModals } from 'wmic-pe-portals-wizard-components-ui';
import { policyRenewalMessages as commonMessages } from 'gw-capability-policyrenewal-common-react';
import { messages as commonRenewalMessages } from 'wmic-pe-capability-gateway-policyrenewal-common-react';
import { CONSTANTS, MODAL_CONSTANTS, WIZARD_STEP_PATHS, WMICValidationUtil } from 'wmic-pe-portals-utils-js';
import wizardConfig from './config/ho-renewal-wizard-config.json5';
import wizardStepToFieldMapping from './config/ho-wizard-step-to-field-mapping.json5';
import messages from './WMICPEHORenewalWizard.messages';

function checkValidity(jobVM, extractVMObject, currentStep) {
    const checkValidityPathsForCurrentStep = _.get(currentStep, 'checkValidityPaths', []);

    // Cloning checkValidityPaths to avoid adding duplicates to array
    const checkValidityPaths = _.clone(checkValidityPathsForCurrentStep);

    // For Risk step, validate only dwelling.yourHome path for each dwelling child
    if (currentStep.path === WIZARD_STEP_PATHS.HO.RISKS) {
        if (!_.get(jobVM, 'isEditingPage.value', false)) {
            return true;
        }

        const dwellings = _.get(jobVM, 'lobData.homeowners.coverables.dwellings.children');

        // eslint-disable-next-line no-plusplus
        for (let index = 0; index < dwellings.length; index++) {
            const validityPath = `lobData.homeowners.coverables.dwellings.children[${index}].yourHome`;

            checkValidityPaths.push(validityPath);
        }
    }

    // For Construction step, only validate if user has started editing the page
    if (currentStep.path === WIZARD_STEP_PATHS.HO.CONSTRUCTION) {
        if (!_.get(jobVM, 'isEditingPage.value', false)) {
            return true;
        }
    }

    return WMICValidationUtil.checkValidity(jobVM, checkValidityPaths);
}

function getTargetPageIndex(steps) {
    for (let stepIndex = 0; stepIndex < steps.length; stepIndex += 1) {
        if (steps[stepIndex].id === 'renewalHOQuotePage') {
            return stepIndex;
        }
    }

    return undefined;
}

function WMICPEHORenewalWizard(props) {
    const { steps } = wizardConfig;
    const [initialSubmission, setInitialSubmission] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [hasErrorOccurred, setHasErrorOccurred] = useState(false);
    const viewModelService = useContext(ViewModelServiceContext);
    const { RenewalService } = useDependencies('RenewalService');
    const history = useHistory();
    const location = useLocation();
    const { authHeader } = useAuthentication();
    const translator = useContext(TranslatorContext);
    const { showConfirm, showWarning, setDefaultMessage } = useWizardModals();
    const [canJumpToWizardStepState, setCanJumpToWizardStepState] = useState({
        allowUserToJumpForward: true,
        targetStep: getTargetPageIndex(steps),
    });
    useEffect(
        () => {
            if (!location.state) {
                history.push('/');

                return;
            }

            setDefaultMessage(translator(messages.savingTransactionDetails));

            const {
                state: { policyNumber },
            } = location;
            const { state } = location;

            if (!policyNumber) {
                return;
            }

            const errorModalBox = errorMessage => {
                showConfirm(errorMessage).then(results => {
                    if (results === CONSTANTS.MODAL_RESULT.CANCEL || results === CONSTANTS.MODAL_RESULT.CLOSE) {
                        return _.noop();
                    }

                    setIsLoading(false);

                    return history.push(`/contactAgent/${state.policyNumber}/${state.selectedTerm}`);
                }, _.noop);
            };

            RenewalService.retrieve(state.renewalentry.jobID, authHeader)
                .then(response => {
                    const renewal = viewModelService.create(
                        response,
                        'pc',
                        'wmic.edge.ca.capabilities.renewal.dto.RenewalDataDTO'
                    );

                    steps.forEach(step => _.set(step, 'stepToFieldMapping', wizardStepToFieldMapping[step.id]));

                    setInitialSubmission(renewal);
                    setIsLoading(false);
                })
                .catch(() => {
                    errorModalBox({
                        title: commonMessages.unableToLoadDraftPolicy,
                        message: commonMessages.somethingWentWrong,
                    });
                })
                .catch(() => {
                    errorModalBox({
                        title: commonMessages.unableToStartPolicy,
                        message: commonMessages.anErrorOccurred,
                        status: MODAL_CONSTANTS.STATUS.WARNING,
                        icon: MODAL_CONSTANTS.ICON.WARNING,
                    });
                });
        },
        // Disabled so we don't rerun this function on every rerender
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    );

    const handleOnCancel = useCallback(
        cancelParams => {
            const { wizardData, wizardSnapshot, param } = cancelParams;

            let lastPathName = '';
            const nextPath = param || '';
            const status = _.get(wizardData.value, 'status');
            const jobID = _.get(wizardData.value, 'jobID');

            if (!_.isEmpty(nextPath)) {
                lastPathName = nextPath.replace(/\/?.*\//g, '');
            }

            if (
                (status === 'Bound' && !_.isNil(jobID)) ||
                (!_.isEmpty(lastPathName) && CONSTANTS.RENEWAL_SKIP_CANCEL_MODAL_URLS.includes(lastPathName)) ||
                hasErrorOccurred
            ) {
                return true;
            }

            return showConfirm({
                title: commonRenewalMessages.leaveWithoutSaving,
                message: commonRenewalMessages.infoWillBeSavedAsDraft,
                status: MODAL_CONSTANTS.STATUS.WARNING,
                icon: MODAL_CONSTANTS.ICON.WARNING,
                cancelButtonText: platformMessages.cancelModel,
            }).then(results => {
                if (results === CONSTANTS.MODAL_RESULT.CANCEL || results === CONSTANTS.MODAL_RESULT.CLOSE) {
                    return _.noop();
                }

                setIsLoading(true);

                const isValid = wizardData.aspects.valid && wizardData.aspects.subtreeValid;
                const payload = isValid ? _.get(wizardData, 'value') : _.get(wizardSnapshot, 'value');
                const jobID = _.get(wizardData.value, 'jobID');
                const quoteStatus =
                    _.get(wizardSnapshot, 'baseData.periodStatus.value.code') || _.get(wizardData.value, 'status');
                const isStatusQuoted = quoteStatus !== 'Quoted';
                let exitPromise;
                const redirectLobPath = (!_.isEmpty(nextPath) && nextPath) || `/renewal/${jobID}/summary`;

                if (isStatusQuoted) {
                    exitPromise = RenewalService.saveWithNoValidationRuleCheck(payload, authHeader);
                } else {
                    return history.push(redirectLobPath);
                }

                exitPromise
                    .then(() => {
                        history.push(redirectLobPath);
                    })
                    .catch(() => {
                        showConfirm({
                            title: commonRenewalMessages.unableToDraftRenewal,
                            message: commonRenewalMessages.anErrorOccurred,
                            status: MODAL_CONSTANTS.STATUS.WARNING,
                            icon: MODAL_CONSTANTS.ICON.WARNING,
                        });
                    })
                    .finally(() => {
                        setIsLoading(false);
                    });

                return true;
            }, _.noop);
        },
        [hasErrorOccurred, showConfirm, RenewalService, authHeader, history]
    );

    const handleError = useCallback(
        error => {
            const isQuotePage = _.includes(_.toLower(_.get(error, 'gwInfo.method')), 'quote');
            const jobIDFromWizard = _.get(error, 'gwInfo.params[0].jobID');
            const jobIDFromLocation = _.get(location, 'state.policyNumber');
            const termNumberFromLocation = _.get(location, 'state.selectedTerm');
            let redirectPath = `/contactAgent/${jobIDFromLocation}/${termNumberFromLocation}`;

            if (!_.isEmpty(jobIDFromWizard)) {
                redirectPath = `/renewa/${jobIDFromWizard}/summary`;
            }

            const state = {
                pathname: redirectPath,
                state: {
                    redirectPath: '/home',
                },
            };

            return showWarning({
                title: isQuotePage ? messages.saveQuoteError : messages.anErrorOccurredTitle,
                message: isQuotePage ? messages.saveQuoteErrorMessage : messages.anErrorOccurred,
                status: MODAL_CONSTANTS.STATUS.WARNING,
                icon: MODAL_CONSTANTS.ICON.WARNING,
                confirmButtonText: platformMessages.ok,
            }).then(() => {
                setHasErrorOccurred(true);

                return <Redirect to={state} />;
            }, _.noop);
        },
        [location, showWarning]
    );

    if (isLoading) {
        return <Loader loaded={!isLoading} />;
    }

    if (!initialSubmission) {
        return null;
    }

    return (
        <ErrorBoundary onError={handleError}>
            <Wizard
                initialSteps={steps}
                wizardTitle={translator(messages.progress)}
                initialData={initialSubmission}
                onCancel={handleOnCancel}
                onPreviousModalProps={{
                    title: commonRenewalMessages.wantToJump,
                    message: commonRenewalMessages.wantToJumpMessage,
                    status: MODAL_CONSTANTS.STATUS.WARNING,
                    icon: MODAL_CONSTANTS.ICON.WARNING,
                    confirmButtonText: platformMessages.yes,
                    cancelButtonText: platformMessages.no,
                }}
                checkValidity={checkValidity}
                skipCompletedSteps
                wizardStepToFieldMapping={wizardStepToFieldMapping}
                canJumpToWizardStepState={canJumpToWizardStepState}
                setCanJumpToWizardStepState={setCanJumpToWizardStepState}
            />
        </ErrorBoundary>
    );
}

WMICPEHORenewalWizard.propTypes = {
    location: PropTypes.shape({
        state: PropTypes.shape({
            address: PropTypes.shape({}),
            policyNumber: PropTypes.string,
            requestType: PropTypes.string,
            policyDetails: PropTypes.shape({
                effective: PropTypes.instanceOf(Date),
            }),
        }),
    }).isRequired,
    history: PropTypes.shape({
        push: PropTypes.func,
    }).isRequired,
    authHeader: PropTypes.shape({
        Authorization: PropTypes.string,
    }).isRequired,
};

export default withViewModelService(WMICPEHORenewalWizard);
